// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import OperatingManualContent from "../OperatingManualContent/OperatingManualContent";
import GradientHeader from "src/components/shared/displays/GradientHeader/GradientHeader";
import Alert from "src/components/shared/alerts/Alert/Alert";
import {
  CrosshairIcon,
  EditIcon,
  UserEditIcon,
  TrashIcon,
  HeartbeatIcon,
} from "src/assets/icons";

import forgotPasswordImg from "src/assets/images/operating-manual/faq/forgot-password.png";
import recenterButtonImg from "src/assets/images/operating-manual/instructions-for-use/recenter-button.png";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

type FAQType = {
  title: string;
};

const FAQ = ({ title }: FAQType) => {
  const { t } = useTranslation("operating-manual");

  const faqContent = [
    {
      title:
        "I want to start a new exam, but the VR device is still linked to a previous exam. How do I fix this?",
      route: "faq-new-exam",
      component: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Alert
            variant="info"
            alertContent={t(
              "Note: If an exam has already started you will not be able to link the device to another exam."
            )}
          />
          <Typography>
            {t(
              "In order to link a new exam, you first need to unlink the previous test. To do this, unlink the VR device by pressing the 'unlink' button in the device card. You can find the device card in the bottom left corner of your screen. Once unlinked, refresh the device using the trigger button on the controller."
            )}
          </Typography>
        </Box>
      ),
    },
    {
      title: "Why can't I see the controller?",
      route: "faq-controller-visibility",
      component: (
        <Typography>
          {t(
            "The controller may not be turned on. Press the power button for 1 second."
          )}
        </Typography>
      ),
    },
    {
      title: "Why does the room in the headset now look off-centre?",
      route: "faq-room-off-centre",
      component: (
        <Typography>
          {t(
            "The scene is loaded for your initial position. If you have moved, the scene needs to be recentered. To do this, press the"
          )}
          <Box
            component="img"
            src={recenterButtonImg}
            alt="recenter-button"
            sx={{ mx: "8px" }}
            style={{
              display: "inline",
              width: "45px",
              height: `${UI_SIZES.iconButtonSize}px`,
              verticalAlign: "middle",
            }}
          />
          {t("recenter button on the controller. You can also click the ")}
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <CrosshairIcon />
          </IconButton>
          {t(" icon in the expanded exam view.")}
        </Typography>
      ),
    },
    {
      title:
        "The VR device is not working and I want to reset it. What should I do?",
      route: "faq-vr-reset",
      component: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Typography>
            {t(
              "Here are the following things you can try, depending on the problem:"
            )}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography>
              <span style={{ fontWeight: 600 }}>1. </span>
              {t("Unlink the device if an exam is linked to it (see FAQ 8.1)")}
            </Typography>
            <Typography>
              <span style={{ fontWeight: 600 }}>2. </span>
              {t(
                "Refresh the VR device using the trigger button on the controller."
              )}
            </Typography>
            <Typography>
              <span style={{ fontWeight: 600 }}>3. </span>
              {t("Turn the VR device off and back on.")}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      title: "I forgot my password. How do I reset it?",
      route: "faq-reset-password",
      component: (
        <Grid container spacing="20px">
          <Grid item xs={12} sm={7}>
            <Typography>
              {t(
                "To reset your password, use the 'Forgot Password' button on the login screen. From here, you can type in your email address and follow the instructions to reset your password."
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5}>
            <img src={forgotPasswordImg} width="100%" alt="forgot-password" />
          </Grid>
        </Grid>
      ),
    },
    {
      title:
        "How can I edit the settings of an exam that I've already started?",
      route: "faq-edit-exam-settings",
      component: (
        <Typography>
          {t(
            "To edit the exam after it's started, you'll need to terminate the test. You can do this from the portal by opening the "
          )}
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <HeartbeatIcon />
          </IconButton>
          {t(" live update panel and pressing ")}
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: "#5B6D7A",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <TrashIcon />
          </IconButton>
          {t(" Terminate. Press the ")}
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: "#202338",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <EditIcon />
          </IconButton>
          {t(" button to edit.")}
        </Typography>
      ),
    },
    {
      title:
        "I administered an exam under the wrong patient. How do I move it to the correct patient?",
      route: "faq-move-exam-patient",
      component: (
        <Typography>
          {t("You can use the ")}
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: "#202338",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <UserEditIcon />
          </IconButton>
          {t(
            " Transfer button next to the completed exam. Press on the button, and type in the name of the patient you'd like to transfer it to. It is very important to double check that you have selected the right patient before pressing confirm."
          )}
        </Typography>
      ),
    },
    {
      title:
        "I don't see the total or pattern deviation results for a visual field. Why is that?",
      route: "faq-deviation-results",
      component: (
        <Typography>
          {t(
            "If you're looking at a visual field, and it's missing total/pattern deviation results, it's likely for two reasons: 1. You did a screening test, not a threshold test. 2. You selected red stimulus. Total and pattern deviation results are not available for red stimulus threshold exams."
          )}
        </Typography>
      ),
    },
    {
      title: "The controller(s) is not working. What should I do?",
      route: "faq-controller-not-working",
      component: (
        <Typography>
          {t(
            "Take a look at the controller light when you try to turn it on: Flashing red or no light at all - This usually means the batteries are low or dead. Try replacing them and see if it reconnects. Flashing blue while the headset is ON and ONLINE - This means the controller became unpaired. This can happen if the controller power button and trigger button are pressed at the same time. Totally fixable! Please give us a call or text at 587-741-4575, and we'll help you pair it in under 10 minutes."
          )}
        </Typography>
      ),
    },
    {
      title: "The exam won't link to the headset.",
      route: "faq-exam-wont-link",
      component: (
        <Typography>
          {t(
            "Make sure the headset is turned on and connected to Wi-Fi. Try refreshing the exam list or restarting the headset. If it still doesn't work, contact us at support@retinalogik.ca."
          )}
        </Typography>
      ),
    },
    {
      title: "How do I turn on the controller?",
      route: "faq-turn-on-controller",
      component: (
        <Typography>
          {t(
            "You can turn it on by pressing the power button - the bottom button on the face of the headset that looks like a yin and yang sign!"
          )}
        </Typography>
      ),
    },
    {
      title: "How long should the battery last on the headset?",
      route: "faq-battery-life",
      component: (
        <Typography>
          {t(
            "If not left turned on, it will die after 2 hours. It's important to charge and plug in when not in use."
          )}
        </Typography>
      ),
    },
    {
      title: "Is it ok to leave the headset on and charging overnight?",
      route: "faq-charging-overnight",
      component: (
        <Typography>
          {t(
            "Yes! Just make sure the fan on the front of the headset is not blocked, and it has room to push the hot air away (Leave the case open)."
          )}
        </Typography>
      ),
    },
    {
      title: "How do I add another user to the account?",
      route: "faq-add-user",
      component: (
        <Typography>
          {t(
            "Send us an email at support@retinalogik.ca with your clinic name and the email you'd like to add, and we'll get it set up!"
          )}
        </Typography>
      ),
    },
    {
      title: "The headset is saying offline, when it's not, what do I do?",
      route: "faq-headset-offline",
      component: (
        <Typography>
          {t(
            "First, look in the headset, and confirm it's in the lobby. It's possible you pressed the menu button on the controller, which took you to the menu - if it's there, it will say it's offline when it's turned on. If that isn't the problem send us an email at support@retinalogik.ca."
          )}
        </Typography>
      ),
    },
    {
      title: "How do I connect to wifi?",
      route: "faq-connect-wifi",
      component: (
        <Typography>
          {t(
            "Check out this youtube tutorial: https://www.youtube.com/watch?v=rIs3jkEmuWg&list=PLw534RvTuE0lgBCPzliGxyH5OVjbuwZgu&index=3"
          )}
        </Typography>
      ),
    },
    {
      title:
        "I'm connected to Wi-Fi, but it says the password is wrong or it won't connect. What should I do?",
      route: "faq-wifi-password",
      component: (
        <Typography>
          {t(
            "No worries, let's figure this out together! 1. First, double-check the Wi-Fi password - Try logging into the same Wi-Fi network on another device (like your phone or laptop) using the exact same password. If it connects: Great - the password is correct. If it doesn't: The issue is likely a typo with the password. Confirm the correct password, and try again. 2. Test the headset using a hotspot - Turn on your phone's hotspot and wait 1 to 2 minutes until it appears in the Wi-Fi list on the headset. Try connecting to it. If it connects: The headset is working, the issue is with the clinic Wi-Fi. If it doesn't: We may need to look deeper into the device or settings. Contact support. 3. If it is your clinic Wi-Fi, try restarting the modem/router, and if it still doesn't work, check these settings: Your IT team can quickly confirm the following: Wi-Fi Security: WPA2 or WPA3, PMF (Protected Management Frames): Required, Band Steering: Off, BSS Transition: Off, MAC Address Filtering: Ensure the headset's MAC address is not blocked. Ask us if you need the MAC address. 4. After any changes: Restart the headset and try reconnecting."
          )}
        </Typography>
      ),
    },
    {
      title: "It says no devices available. What should I do?",
      route: "faq-no-devices",
      component: (
        <Typography>
          {t(
            "This means an exam is already linked to the headset, and you need to kick it out to start a new one before finishing this one! To fix it, please unlink the headset by pressing the unlink button at the bottom of the screen."
          )}
        </Typography>
      ),
    },
    {
      title: "Do I need to enter prescription into the patient information?",
      route: "faq-prescription",
      component: (
        <Typography>
          {t(
            "No, you do not. The prescription information is optional, in the future it will calculate the trial lens you can select for the patient to use with our trial lens attachment."
          )}
        </Typography>
      ),
    },
    {
      title: "Should patients wear glasses/correction with the headset?",
      route: "faq-glasses",
      component: (
        <Typography>
          {t(
            "The system uses the patient's distance Rx and simulates optical infinity inside the headset, so most patients don't need their glasses. No glasses are required up to approximately 4.00D sphere and up to -2.00D cylinder. However, if they have distance glasses, they should wear them."
          )}
        </Typography>
      ),
    },
    {
      title: "What's the best lighting for the headset?",
      route: "faq-lighting",
      component: (
        <Typography>
          {t(
            "Ambient lighting is best, this lets the eye tracking cameras see the patient's eyes better, without affecting the results."
          )}
        </Typography>
      ),
    },
    {
      title: "What's the best way to put on the headset?",
      route: "faq-headset-fitting",
      component: (
        <Typography>
          {t(
            "Check out these tutorial videos on how to use the system, they'll walk you through how to properly put on the headset. https://www.youtube.com/watch?v=SvE1LQME-rw&list=PLw534RvTuE0lgBCPzliGxyH5OVjbuwZgu&index=6"
          )}
        </Typography>
      ),
    },
    {
      title: "How can I switch devices between clinics I have access to?",
      route: "faq-transfer-devices",
      component: (
        <Typography>
          {t(
            "Click the three dots in the top right of the device card to see device options, then click the transfer device button."
          )}
        </Typography>
      ),
    },
  ];

  return (
    <OperatingManualContent title={title}>
      {faqContent.map((content, index) => (
        <Box
          id={content.route}
          key={content.route}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <GradientHeader header={`8.${index + 1} ${t(content.title)}`} />
          <Box sx={{ mx: "20px" }}>{content.component}</Box>
        </Box>
      ))}
    </OperatingManualContent>
  );
};

export default FAQ;
